/**
 * User Service (Frontend)
 * API calls för användarhantering
 */

import api from '../api/client';

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  /** RBAC-rollkod (roles.code) – för visning. */
  role: string | null;
  /** RBAC-rollens visningsnamn (roles.name). */
  role_name?: string | null;
  /** Alla aktiva RBAC-rollkoder. */
  roleCodes?: string[];
  organization_id: string;
  organization_name: string;
  preferred_language: string;
  auth_method: 'PASSWORD' | 'TWO_FACTOR' | 'SAML2';
  is_global_admin: boolean;
  MobilePhone: string;
  driver: {
    id: number;
    license_number: string | null;
    license_expiry: string | null;
    license_categories: string | null;
    employee_id: string | null;
    department: string | null;
    cost_center: string | null;
  } | null;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  MobilePhone?: string;
  current_password?: string;
  new_password?: string;
}

/**
 * Användarpreferenser (lagras som JSON i users.preferences).
 * Utökas successivt med fler inställningar.
 */
export interface UserPreferences {
  /** Förvald utlämningsplats för den inloggade användaren (booking-modulen). */
  defaultPickupLocationId?: string | null;
  /** Stjärnmärkta moduler som `${systemName}:${moduleName}`. */
  favoriteModuleKeys?: string[];
  [key: string]: unknown;
}

export const userService = {
  /**
   * Hämta nuvarande användares profil (utökad med driver-info)
   */
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get('/api/users/me');
    return response.data.data;
  },

  /**
   * Uppdatera den inloggade användarens profil
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<UserProfile> => {
    const response = await api.put('/api/users/me/profile', data);
    return response.data.data;
  },

  /**
   * Uppdatera användarens språkval
   */
  updateLanguage: async (language: string): Promise<void> => {
    await api.put('/api/users/me/language', { language });
  },

  /**
   * Hämta användarens språkval
   */
  getLanguage: async (): Promise<string> => {
    const response = await api.get('/api/users/me/language');
    return response.data.data.language;
  },

  /**
   * Hämta användarens preferenser (JSON)
   */
  getPreferences: async (): Promise<UserPreferences> => {
    const response = await api.get('/api/users/me/preferences');
    return (response.data?.data ?? response.data ?? {}) as UserPreferences;
  },

  /**
   * Uppdatera (merge) användarens preferenser
   */
  updatePreferences: async (prefs: Partial<UserPreferences>): Promise<UserPreferences> => {
    const response = await api.put('/api/users/me/preferences', prefs);
    return (response.data?.data ?? response.data ?? {}) as UserPreferences;
  },
};

